import { Container, Coords, RenderPosition } from '../../shared/interfaces';
import { cssNamespace } from '../../shared/styles';
import { applyZIndex } from '../utils/builder';
import { DEFAULT_SPACING_BOTTOM, Magnetize } from '../utils/magnetize';
import { MagnetizeElise, observeEliseWidget } from '../utils/magnetize-elise';

const DEFAULT_WIDGET_WIDTH = 210;
const DEFAULT_TOOLTIP_PADDING_Y = 10;
export const MAX_COMPACT_HEIGHT_MOBILE = 72;
export const MAX_COMPACT_HEIGHT_DESKTOP = 120;
export const MAX_COMPACT_HEIGHT_WITH_ACTIONS = 155;
const DEFAULT_Z_INDEX = 1000;

export interface CompactListingConfig {
  widget_zindex?: number;
}

export interface CompactConfig {
  marginX: number;
  marginY: number;
  position: RenderPosition;
  listing: CompactListingConfig;
  screen: { isMobile: boolean };
  syncEliseAI?: boolean;
  renderAs?: string;
  [key: string]: unknown;
}

export abstract class BaseCompact extends Container {
  protected config: CompactConfig;
  protected elId: string;
  protected element: HTMLDivElement | null;
  protected tooltip: HTMLDivElement | null;
  protected magnetize: Magnetize | null;
  protected eliseObserver: MutationObserver | null;

  constructor(elId: string) {
    super();

    this.config = {} as CompactConfig;
    this.elId = elId;
    this.element = null;
    this.tooltip = null;
    this.magnetize = null;
    this.eliseObserver = null;
  }

  onOpen() {
    this.element?.classList.add('is-hidden');
  }

  onClose() {
    this.element?.classList.remove('is-hidden');
  }

  onToggle() {
    this.element?.classList.toggle('is-hidden');
  }

  protected hideTooltip() {
    if (this.tooltip) {
      this.tooltip.style.display = 'none';
    }
  }

  protected getTooltipElement(): HTMLDivElement {
    if (!this.tooltip) {
      this.tooltip = document.createElement('div');
      this.tooltip.classList.add(`${cssNamespace}tooltip`);
      document.body.appendChild(this.tooltip);
    }

    return this.tooltip;
  }

  onTooltipDisplay = (coordinates: Coords | null, label: string) => {
    if (!coordinates || !label) {
      this.hideTooltip();

      return;
    }

    const tooltip = this.getTooltipElement();
    tooltip.style.left = '';
    tooltip.style.right = '';

    const widgetWidth = DEFAULT_WIDGET_WIDTH;
    const marginX = this.config?.marginX ?? 0;
    const marginY = this.config?.marginY ?? 0;
    const positionKey = this.config?.position ?? 'right';

    tooltip.textContent = label;
    tooltip.style[positionKey] = `${widgetWidth + marginX}px`;
    tooltip.style.bottom = `calc(${DEFAULT_TOOLTIP_PADDING_Y}px + var(--rg-magnetize-bottom, ${marginY}px))`;
    tooltip.style.display = 'flex';
  };

  setMargins() {
    if (!this.element) return;

    this.element.style[this.config.position] = `${this.config.marginX}px`;
    this.element.style.bottom = `${this.config.marginY}px`;
  }

  setZIndex() {
    if (!this.element) return;

    applyZIndex(
      this.element,
      this.config.listing?.widget_zindex ?? DEFAULT_Z_INDEX
    );
  }

  protected setHeight(hasNoActionButtons: boolean) {
    if (!this.element) return;

    this.element.style.height = hasNoActionButtons
      ? `${MAX_COMPACT_HEIGHT_MOBILE}px`
      : `${MAX_COMPACT_HEIGHT_DESKTOP}px`;
  }

  protected setupMagnetization() {
    if (!this.element) return;

    if (this.config.syncEliseAI) {
      this.syncWithElise();
    } else {
      this.setupOnetrustMagnetize();
    }
  }

  protected teardownMagnetization() {
    this.disconnectEliseObserver();
    this.cleanupMagnetize();
  }

  unmount() {
    this.teardownMagnetization();
  }

  private syncWithElise() {
    if (!this.element) return;

    const initializeMagnetize = () => {
      if (!this.element) return;

      this.cleanupMagnetize();
      this.magnetize = new MagnetizeElise(this.element, {
        bottom: this.config.screen.isMobile ? 0 : DEFAULT_SPACING_BOTTOM,
        right: 0,
      });
      this.magnetize.init();
    };

    const eliseElement = MagnetizeElise.getEliseElement();

    if (eliseElement) {
      initializeMagnetize();
      return;
    }

    this.eliseObserver = observeEliseWidget(() => {
      initializeMagnetize();
    });
  }

  private setupOnetrustMagnetize() {
    if (!this.element) return;

    const target = document.getElementById('onetrust-banner-sdk');
    if (!target) return;

    this.cleanupMagnetize();
    this.magnetize = new Magnetize(target, this.element, {
      bottom: this.config.marginY,
    });
    this.magnetize.init();
  }

  private disconnectEliseObserver() {
    if (this.eliseObserver) {
      this.eliseObserver.disconnect();
      this.eliseObserver = null;
    }
  }

  private cleanupMagnetize() {
    if (this.magnetize) {
      this.magnetize.cleanup();
      this.magnetize = null;
    }
  }
}
